import { Component, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    AuthService,
    DataSources,
    EmployeesClient, PermissionType,
    ProfileSupplierDto,
    UpdateProfileSupplierCommand
} from '@services';
import {
    DxTagBoxModule,
    DxValidationGroupComponent,
    DxValidationGroupModule,
    DxValidatorModule
} from 'devextreme-angular';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import notify from 'devextreme/ui/notify';

@Component({
    selector: 'app-supplier-details',
    templateUrl: './supplier-details.component.html',
    standalone: true,
    imports: [
        CommonModule,
        DxTagBoxModule,
        DxValidationGroupModule,
        DxValidatorModule
    ]
})
export class SupplierDetailsComponent implements OnInit {
    private employeesClient = inject(EmployeesClient);
    private dataSources = inject(DataSources);
    protected authService = inject(AuthService);

    @Input() userId: string;
    @Input() forceReadOnly = false;
    @Output() isDataChanged = new EventEmitter<boolean>();

    @ViewChild('validationGroup', { static: false }) validationGroup: DxValidationGroupComponent;

    profileDetails: ProfileSupplierDto;
    isLoading = false;

    protected supplierDataSource = this.dataSources.supplierOptions();

    ngOnInit(): void {
        this.loadData();
    }

    loadData() {
        if (!this.userId) return;
        this.isLoading = true;
        this.employeesClient.getProfileSupplier(this.userId).subscribe({
            next: (data) => {
                this.profileDetails = data;
                this.isLoading = false;
            },
            error: () => {
                this.isLoading = false;
                notify('Failed to load supplier details', 'error', 3000);
            }
        });
    }

    public save(): Observable<void> {
        if (this.isReadOnly) return of(void 0);
        if (!this.validationGroup) return of();

        const result = this.validationGroup.instance.validate();
        if (!result.isValid) {
            notify({ message: 'Please fix supplier details errors', position: 'bottom center' }, 'error', 3000);
            return throwError(() => new Error('Validation failed'));
        }

        const command = new UpdateProfileSupplierCommand({
            userId: this.userId,
            suppliers: this.profileDetails.suppliers
        });

        return this.employeesClient.updateProfileSupplier(command).pipe(
            catchError((err) => {
                const errors = err?.response?.errors || [{ message: 'Failed to update supplier details' }];
                errors.forEach((errMsg: any) => {
                    notify({
                        message: errMsg.message || errMsg,
                        type: "error",
                        displayTime: 5000,
                    }, {
                        position: "bottom right",
                        direction: "up-push"
                    });
                });
                return throwError(() => err);
            })
        );
    }

    onFieldChange() {
        this.isDataChanged.emit(true);
    }

    get isReadOnly(): boolean {
        return this.forceReadOnly || !this.authService.canEditProfileSection(this.userId, PermissionType.UserSupplierDetailsOwn, PermissionType.UserSupplierDetailsOthers);
    }
}
